import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { NgClass, NgStyle } from '@angular/common';
import { Component, DestroyRef, ElementRef, Input, OnInit, QueryList, ViewChildren, inject, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { CmsItem } from '@coin/shared/cms/util';
import { IconButtonComponent, V2ButtonComponent, V2TextInputComponent } from '@coin/shared/core/ui-storybook';
import { GlobalEventsService } from '@coin/shared/core/util-helpers';
import { TranslateModule } from '@ngx-translate/core';
import { SelectMediaComponent } from '../../../dialogs/shared/select-media/select-media.component';

@Component({
  selector: 'coin-gmm-picture-container',
  templateUrl: './picture-container.component.html',
  styleUrls: ['./picture-container.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    DragDropModule,
    NgClass,
    V2ButtonComponent,
    NgStyle,
    MatFormFieldModule,
    TranslateModule,
    IconButtonComponent,
    FormsModule,
    V2TextInputComponent
  ]
})
export class PictureContainerComponent implements OnInit {
  private readonly dialog = inject(MatDialog);
  private readonly events = inject(GlobalEventsService);
  private readonly destroyRef = inject(DestroyRef);

  @Input() item: CmsItem<'picture-container'>;
  @Input() contentEdit = false;
  @Input() isMobile = false;
  @Input() heightModified = false;
  @Input() otherPadding = false;

  public containerLength = 0;
  public scrollPosition = 0;
  public refresh = false;

  @ViewChildren('item') itemsList: QueryList<ElementRef<HTMLDivElement>>;

  public overflow: 'hidden' | 'auto' = 'auto';

  ngOnInit(): void {
    if (!this.contentEdit) {
      this.overflow = 'hidden';
    }
    this.containerLength = this.item?.pictureContainers ? this.item?.pictureContainers.length : 99999;
    this.checkContainerLength();
    this.events
      .listen('resize')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.onResize());
  }

  public checkContainerLength(): void {
    setTimeout(() => {
      this.containerLength = this.item?.pictureContainers ? this.item?.pictureContainers.length : 99999;
      this.checkContainerLength();
    }, 3000);
  }

  public deleteItem(index: number): void {
    this.item.pictureContainers.splice(index, 1);
  }

  public drop(event: CdkDragDrop<string[]>, list: PictureContainerComponent['item']['pictureContainers']): void {
    moveItemInArray(list, event.previousIndex, event.currentIndex);
  }

  public editPicture(index: number): void {
    SelectMediaComponent.openDialog(this.dialog, { url: this.item.pictureContainers[index]?.img, mediaType: 'image' })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(newImage => {
        if (newImage) {
          this.item.pictureContainers[index].img = newImage.mediaUrl;
        }
      });
  }

  public onResize = (): void => {
    const container = document.getElementsByClassName('picture-cont-drag-list')[0] as HTMLElement;
    container.scrollTo({ left: 0, behavior: 'smooth' });
  };

  public addImage(): void {
    this.item.pictureContainers.push({
      img: '',
      subTitle: 'New Picture Container Subtitle',
      mainTitle: 'New Picture Container Main Title',
      text: 'New Picture Container Text',
      link: ''
    });
  }

  public scroll(direction: 'left' | 'right'): void {
    if (this.containerLength > 1) {
      this.refresh = true;
      const el = document.getElementsByClassName('picture-cont-drag-box')[1] as HTMLElement;
      const { width } = el.getBoundingClientRect();
      const container = document.getElementsByClassName('picture-cont-drag-list')[0] as HTMLElement;
      if (direction === 'right') {
        container.scrollBy({ top: 0, left: width, behavior: 'smooth' });
      } else {
        container.scrollBy({ top: 0, left: -width, behavior: 'smooth' });
      }
      setTimeout(() => {
        this.refresh = false;
      }, 400);
    }
  }

  public showArrow(arrow: 'left' | 'right'): boolean {
    const container = document.getElementsByClassName('picture-cont-drag-list')[0] as HTMLElement;
    if (arrow === 'left') {
      return container?.scrollLeft > 20;
    }
    const scrollWidth = container?.scrollWidth;
    const offsetWidth = container?.offsetWidth;
    const scrollLeft = container?.scrollLeft;
    return scrollWidth - scrollLeft > offsetWidth;
  }
}
